import { useMemo } from 'react';
import { FormSingleSelect } from '@components/forms/dropdowns/base/FormSingleSelect';
import { keymap } from '@utils/helper';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';

import type { SyntheticEvent } from 'react';
import type { FormComponentProps } from '../types';

const defaults = {
    reseller: { ResellerID: null, ResellerName: '' },
    location: { LocationID: null, LocationName: '' },
};

type DropProps<State extends Record<string, any>> = FormComponentProps<State> & {
    options: {
        companies: boolean;
        resellers: boolean;
        locations: boolean;
    };
    size?: number;
    disabled?: boolean;
    includeAll?: boolean;
};

export const OrgDropDowns = <State extends Record<string, any>>({
    draft,
    options,
    size,
    disabled = false,
    includeAll = false,
}: DropProps<State>) => {
    const [draftState, setDraftState] = draft;

    if (options.resellers && !options.companies) {
        throw new Error('Must set options.companies to "true" if options.resellers is set to "true"!');
    }

    const { data: NameMap } = useNameMapQuery({});
    const { data: OrgMap } = useOrgMapQuery({});

    const mlOpts = useMemo(
        () => keymap(NameMap?.companies),
        [NameMap?.companies, OrgMap?.companyResellerMap, OrgMap?.companyLocationMap]
    );
    const rsOpts = useMemo(
        () => keymap(NameMap?.resellers),
        [NameMap?.resellers, OrgMap?.companyResellerMap, OrgMap?.resellerLocationMap]
    );
    const locOpts = useMemo(
        () => keymap(NameMap?.locations),
        [NameMap?.locations, OrgMap?.resellerLocationMap, OrgMap?.companyLocationMap]
    );

    const resetChildren =
        (children: (keyof typeof defaults | false)[], idKey: string, nameKey: string) =>
        (_: SyntheticEvent<Element, Event>, value: any) => {
            const { id, name } = value.pop() ?? {};

            const rsDefault = options.resellers && children.includes('reseller') ? defaults.reseller : {};
            const locDefault = options.locations && children.includes('location') ? defaults.location : {};

            setDraftState({
                ...draftState,
                [idKey]: id !== undefined ? parseInt(id) : null,
                [nameKey]: name,
                ...rsDefault,
                ...locDefault,
            });
        };

    return (
        <>
            {options.companies && (
                <FormSingleSelect
                    disable={disabled}
                    size={size}
                    draft={draft}
                    label='Company'
                    includeAll={includeAll}
                    idKey={'CompanyID'}
                    nameKey={'CompanyName'}
                    options={mlOpts}
                    onChange={resetChildren(['reseller', 'location'], 'CompanyID', 'CompanyName')}
                />
            )}

            {options.resellers && (
                <FormSingleSelect
                    disable={disabled}
                    size={size}
                    includeAll={includeAll}
                    label='Reseller'
                    draft={draft}
                    idKey={'ResellerID'}
                    nameKey={'ResellerName'}
                    options={
                        options.companies
                            ? rsOpts.filter(({ id }) => {
                                  return OrgMap?.companyResellerMap[draftState?.CompanyID]?.includes(+id);
                              })
                            : rsOpts
                    }
                    onChange={resetChildren(['location'], 'ResellerID', 'ResellerName')}
                />
            )}

            {options.locations && (
                <FormSingleSelect
                    disable={disabled}
                    size={size}
                    includeAll={includeAll}
                    draft={draft}
                    label='Location'
                    idKey={'LocationID'}
                    nameKey={'LocationName'}
                    options={locOpts.filter(({ id }) => {
                        return options.resellers && draftState?.ResellerID
                            ? OrgMap?.resellerLocationMap[draftState?.ResellerID]?.includes(+id)
                            : OrgMap?.companyLocationMap[draftState?.CompanyID]?.includes(+id);
                    })}
                    onChange={resetChildren([], 'LocationID', 'LocationName')}
                />
            )}
        </>
    );
};
